import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { socket } from '../lib/socket';
import { EVENTS, type PresenceData } from '@flip7/shared';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface Friend {
  id: string;
  name: string;
  activeAvatar: string | null;
}

interface OnlineFriendsProps {
  onJoinRoom: (roomCode: string) => void;
  onSpectateRoom: (roomCode: string) => void;
}

export function OnlineFriends({ onJoinRoom, onSpectateRoom }: OnlineFriendsProps) {
  const { user, accessToken } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [presences, setPresences] = useState<Map<string, PresenceData>>(new Map());

  useEffect(() => {
    if (!accessToken) return;
    fetch(`${API_BASE}/api/friends`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(res => res.ok ? res.json() : [])
      .then(setFriends)
      .catch(() => {});
  }, [accessToken]);

  // Connect socket if needed, then request presence for friends
  useEffect(() => {
    if (friends.length === 0) return;
    const requestPresence = () => {
      socket.emit(EVENTS.REQUEST_PRESENCE, { userIds: friends.map(f => f.id) });
    };
    if (!socket.connected) {
      socket.connect();
      socket.once('connect', requestPresence);
      return () => { socket.off('connect', requestPresence); };
    } else {
      requestPresence();
    }
  }, [friends]);

  // Listen for presence updates
  useEffect(() => {
    const handleBulk = (data: { presences: PresenceData[] }) => {
      setPresences(prev => {
        const next = new Map(prev);
        for (const p of data.presences) next.set(p.userId, p);
        return next;
      });
    };
    const handleUpdate = (data: PresenceData) => {
      setPresences(prev => new Map(prev).set(data.userId, data));
    };
    socket.on(EVENTS.PRESENCE_BULK, handleBulk);
    socket.on(EVENTS.PRESENCE_UPDATE, handleUpdate);
    return () => {
      socket.off(EVENTS.PRESENCE_BULK, handleBulk);
      socket.off(EVENTS.PRESENCE_UPDATE, handleUpdate);
    };
  }, []);

  if (!user) return null;

  const onlineFriends = friends.filter(f => {
    const p = presences.get(f.id);
    return p?.online;
  });

  if (onlineFriends.length === 0) return null;

  return (
    <div className="online-friends">
      {onlineFriends.map(f => {
        const p = presences.get(f.id);
        const inGame = p?.inGame && p?.roomCode;
        const isLobby = p?.isLobby;
        return (
          <div key={f.id} className="online-friends__item">
            <span className="presence-dot presence-dot--online" />
            <span className="online-friends__name">{f.name}</span>
            {inGame && (
              <button
                className="online-friends__join"
                onClick={() => isLobby ? onJoinRoom(p!.roomCode!) : onSpectateRoom(p!.roomCode!)}
              >
                {isLobby ? 'Join' : 'Watch'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
